// Balance constants, shared between server (authoritative simulation) and
// client (rendering/prediction-free display). Values were first-guess
// placeholders through stages 1-6, tuned during stage 7's playtesting pass
// once every mechanic was in and could actually be felt end-to-end. Still a
// v1 first pass, not a final balance — expect another round once
// spells/items/loot land.

// Checkpoints spawn `CHECKPOINT_SPAWN_AHEAD_ROWS` rows ahead of the frontier
// (N in the design doc). Reaching one adds `CHECKPOINT_TIMER_BONUS_SECONDS`
// (T) to whatever time remains on the countdown; if the countdown ever
// reaches 0, the architect loses. The flame barrier advances at a constant
// rate of N/T tiles/sec — this is what keeps the architect's and heroes'
// pacing aligned by construction. Declared outside CONFIG so the derived
// rate can't drift out of sync with N and T.
const CHECKPOINT_SPAWN_AHEAD_ROWS = 4; // N
const CHECKPOINT_TIMER_BONUS_SECONDS = 20; // T

const STARTING_ROOM_ROWS = 2; // pre-built floor rows at row 0 — must match CONFIG.startingRoomRows below
const HERO_SPAWN_ROW = STARTING_ROOM_ROWS / 2; // must match server/heroes.ts's createHero

// Over one full initial timer duration T, the barrier (advancing at N/T
// tiles/sec) covers exactly N tiles — so starting it at least N tiles
// behind where heroes spawn guarantees it can never reach them before the
// architect's very first timer could possibly expire, even if the
// architect does nothing at all. (Once checkpoints start getting reached the
// timer only gets more forgiving, so the worst case is what matters here.)
// A little extra margin avoids a razor's-edge tie at exactly N.
const FLAME_BARRIER_SAFETY_MARGIN_TILES = 1;
const FLAME_BARRIER_START_ROW = HERO_SPAWN_ROW - CHECKPOINT_SPAWN_AHEAD_ROWS - FLAME_BARRIER_SAFETY_MARGIN_TILES;

export const CONFIG = {
  // --- network ---
  port: 5700,
  tickRateHz: 20,

  // --- dungeon grid ---
  dungeonWidth: 12, // tiles, fixed width of the playing area
  startingRoomRows: STARTING_ROOM_ROWS, // pre-built floor rows at row 0 so heroes have footing before the architect places anything

  // --- camera (client-side render, stage 2+) ---
  // The architect has a wider view than the heroes to better support their
  // gameplay (design doc: "let's do x2 for now") — both sides see the full
  // dungeon width, the difference is how many rows are visible at once.
  heroViewportRows: 14,
  architectViewportRows: 28,

  // --- architect: pieces / placement (stage 2) ---
  pieceSpawnAheadRows: 6, // pieces spawn this many rows ahead of the frontier
  foundationDensity: 0.04, // fraction of void tiles ahead of the frontier that get a foundation obstacle
  minionMarkerDensity: 0.03, // fraction of (non-foundation) void tiles that get a minion marker (stage 5)
  // A hard "wall" behind the front of the dungeon: pieces can't be moved or
  // placed more than this many rows behind the current frontier. Gives some
  // slack to fill in a column that lags behind the overall frontier (an
  // uneven build), without letting the architect wander pieces uselessly
  // far back into already-settled territory.
  architectBehindFrontierRows: 4,
  // The architect isn't allowed to build too far ahead of the heroes
  // either: a piece can't be *placed* with any cell more than this many
  // rows past whichever living hero is furthest along — see
  // server/dungeon.ts's architectPlace. The piece can still be freely
  // moved/rotated past that line to scout ahead, only placement is blocked
  // there. Rendered as a dashed line on the architect's own screen, same
  // idea (and look) as architectBehindFrontierRows' wall line, just marking
  // the front limit instead of the back one.
  architectAheadOfHeroesRows: 11,

  // --- architect: checkpoint timer / flame barrier pacing (stage 2 & 4) ---
  checkpointSpawnAheadRows: CHECKPOINT_SPAWN_AHEAD_ROWS,
  checkpointTimerBonusSeconds: CHECKPOINT_TIMER_BONUS_SECONDS,
  flameBarrierTilesPerSecond: CHECKPOINT_SPAWN_AHEAD_ROWS / CHECKPOINT_TIMER_BONUS_SECONDS,
  // Below this many seconds remaining, the architect's own client plays a
  // warning beep + flashes a screen border — a fixed absolute cutoff (not a
  // percentage of the current timer pool, which can grow well past T after a
  // few checkpoints) since what matters is "seconds until you actually lose".
  checkpointTimerLowSeconds: 10,

  // --- heroes (stage 3 & 6) ---
  // Originally calibrated so crossing one tile takes ~3s (matching N/T's
  // pacing so heroes could just barely outrun the barrier); doubled per
  // playtest feedback — outrunning the barrier now has real slack instead
  // of being a razor's edge, which felt better in practice.
  heroBaseMoveTilesPerSecond: 2 / 3,
  heroBaseMaxHp: 100,
  heroBaseAttackDamage: 10,
  heroBaseAttackRange: 1.5, // tiles
  heroBaseAttackRate: 1.5, // attacks/sec
  // Death is permanent in v1 (see doc/game_design.md's plan notes) — there's
  // no respawn code path at all, so there's no flag to gate it with here.
  heroLevelUpStatIncrements: {
    moveSpeed: 0.07, // tiles/sec
    maxHp: 10,
    attackDamage: 1,
  },
  // Per-minion xp for a *base-size* group (CONFIG.minionGroupSize). When the
  // architect's minionCount upgrade makes a group bigger, each minion in it
  // grants proportionally less xp so the group's *total* xp payout stays the
  // same regardless of how many minions it was split into (see
  // server/simulation.ts's spawnMinionGroup) — otherwise the upgrade would
  // be a strict xp-farming buff for heroes, working against the architect
  // who paid for it. Minions spawned from a paid architect ability
  // (spawnMinions / spawnSuperMinion) grant no xp at all, regardless of this
  // value — only minion-marker activations pay out xp.
  xpPerMinionKill: 15,
  xpToReachLevel: (level: number): number => 100 * level, // linear curve, per design doc example
  // Each hero's auto-attack range circle is shown on everyone's screen for
  // this many seconds after the match starts, so players can learn their
  // class's range without needing to check a wiki — then it fades away so it
  // doesn't clutter the view for the rest of the match.
  heroAttackRangeShowSeconds: 5,

  // --- hero passive regen (design doc: "All classes heal slowly if they
  // have not taken damage for some time") ---
  heroRegenIdleSeconds: 8, // no damage taken for this long before regen kicks in
  heroRegenPerSecond: 3,

  // --- hero classes (design doc's "Classes" section) — each overrides a
  // subset of the heroBase* stats above; anything not listed here (e.g.
  // moveSpeed) stays at the shared base. First-pass balance, invented for
  // v1 same as everything else in this file. ---
  heroClassStats: {
    warrior: { maxHp: 130, attackDamage: 12, attackRange: 1.1, attackRate: 1.3 },
    hunter: { maxHp: 85, attackDamage: 7, attackRange: 5, attackRate: 1.2 },
    mage: { maxHp: 75, attackDamage: 1, attackRange: 4, attackRate: 2 },
  },

  // --- spells (design doc's "Classes" section; slots are 1-indexed,
  // bound to Q/E/R client-side — see src/input.ts) ---
  heroSpells: {
    warrior: {
      // 1: AOE damage-over-time around the caster.
      spell1: { cooldown: 10, radius: 2.2, dotDamagePerSecond: 7, dotDuration: 4 },
      // 2: buff self — reduced damage taken + increased attack rate.
      spell2: { cooldown: 16, duration: 5, damageReductionPct: 0.3, attackRateMult: 1.5 },
      // 3: leap toward the pointer; can cross void.
      spell3: { cooldown: 10, distance: 4 },
    },
    hunter: {
      // 1: piercing skill shot in a line toward the pointer.
      spell1: { cooldown: 10, range: 8, width: 0.7, damage: 16 },
      // 2: glyph AOE around self — increased move speed + attack range for anyone standing in it.
      spell2: { cooldown: 13, radius: 5, duration: 6, moveSpeedMult: 1.4, rangeAdd: 2.5 },
      // 3: target a hero (closest to the pointer, in range), refresh their spell 1's cooldown.
      spell3: { cooldown: 12, range: 8 },
    },
    mage: {
      // 1: glyph AOE at the pointer — damage over time to enemies standing in it.
      spell1: { cooldown: 10, range: 4, radius: 1.8, duration: 3, dotDamagePerSecond: 9 },
      // 2: teleport self + nearby heroes to the pointer.
      spell2: { cooldown: 16, range: 6, allyRadius: 3 },
      // 3: glyph AOE at the pointer — heal over time to heroes standing in it.
      spell3: { cooldown: 14, range: 7, radius: 2, duration: 6, healPerSecond: 9 },
    },
  },
  // Mage auto-attack slows whatever it hits (design doc: "auto-attack: ranged, very low damage, slows down targeted").
  mageAutoAttackSlowFactor: 0.5, // move speed multiplier while slowed
  mageAutoAttackSlowDuration: 1.0, // seconds

  // --- minions (stage 5) ---
  minionBaseHp: 30,
  minionBaseDamage: 8,
  minionBaseAttackRange: 1, // tiles
  minionBaseAttackRate: 1, // attacks/sec
  minionAggroRange: 15, // tiles
  minionMoveTilesPerSecond: 1 / 3,
  minionGroupSize: 3,
  // Super minions (design doc's "Target shapes" gold-spend list) are the
  // same base creature, scaled up — no separate stat block invented.
  superMinionHpMultiplier: 6,
  superMinionDamageMultiplier: 2.5,
  superMinionMoveSpeedMultiplier: 1.2, // slower, to offset being much tankier/harder-hitting
  // Passive difficulty ramp: every level-up any hero earns (summed across
  // the whole party, so a 3-hero party ramps faster than a solo one) nudges
  // future-spawned minions up too — same spirit as the architect's paid
  // permanent upgrades below, but automatic and free, so minions don't stay
  // trivial forever while heroes snowball. Only future-spawned minions are
  // affected (existing ones on the field don't retroactively get stronger),
  // same as architectUpgradePerLevel.
  minionHeroLevelScaling: {
    minionHealth: 0.04, // +4% minion max HP per hero level-up (summed across the party)
    minionDamage: 0.04, // +4% minion attack damage per hero level-up
    minionSpeed: 0.015, // +1.5% minion move speed per hero level-up
  },

  // --- flame barrier damage (stage 4) ---
  // Tuned so a hero standing fully behind the barrier dies in a few seconds,
  // per the design doc.
  flameBarrierDamagePerSecond: 35,
  flameBarrierStartRow: FLAME_BARRIER_START_ROW,
  // The barrier always advances at exactly this constant pace — no catch-up
  // jumps of any kind (a checkpoint getting rescued instead of actually
  // reached, see dungeon.ts's architectPlace, used to bump it forward; that
  // effect has been removed entirely so the lava's pace never depends on
  // what the architect does).

  // --- architect gold economy (design doc's "Target shapes" section) ---
  goldPerMinionMarkerActivation: 15,
  goldPerLineClear: 35,
  goldPerTargetShapeCompleted: 20,
  // Upper bound on architectGold — every *gain* clamps to this (see
  // simulation.ts's addGold); spends and the chest steal-back can still take
  // it below this or even negative, only gains are capped.
  architectGoldCap: 400,
  // Faded 4-tile target shapes: reuses the tetromino shapes as decorative
  // (non-blocking, like minion markers) void-tile groups. Rolled once per
  // generated row, independent of foundation/minion-marker density — but
  // never within targetShapeMinGapRows of the last one, so a lucky run of
  // rolls can't cluster several together while other stretches go quiet
  // (playtest feedback: "grouped up around a row... none for some time").
  targetShapeChancePerRow: 0.4,
  targetShapeMinGapRows: 4,

  architectAbilityCosts: {
    meteor: 60,
    spawnMinions: 35,
    spawnSuperMinion: 90,
  },
  meteorTelegraphSeconds: 3.0,
  meteorRadius: 2.0,
  meteorDamage: 60,
  spawnTelegraphSeconds: 2,
  // How far ahead of the frontier an ability point may target — abilities
  // are meant to bother heroes directly, not seed the far, still-void future.
  architectAbilityMaxAheadRows: 6,

  // Permanent upgrades (design doc: "permanently increase minions' movement
  // speed / health / damage") — repeatable, cost rises each purchase.
  architectUpgradeBaseCost: 40,
  architectUpgradeCostGrowth: 1.0,
  architectUpgradePerLevel: {
    minionSpeed: 0.08, // +8% minion move speed per level
    minionHealth: 0.15, // +15% minion max HP per level
    minionDamage: 0.12, // +12% minion attack damage per level
  },
  // How many extra minions spawn per group, per level — a flat add rather
  // than a multiplier like the other three (a fractional minion doesn't
  // mean anything), applied both to minion-marker activation and the
  // "spawnMinions" ability (not the super-minion spawn, which is always
  // exactly one regardless of this upgrade — see server/simulation.ts).
  architectUpgradeMinionCountPerLevel: 1,

  // --- doors / keys / chests ---
  // A newly-spawned piece has this chance of carrying exactly one special
  // cell (door, key, or chest — see dungeon.ts's rollSpecialCell). Door vs
  // key isn't independently random: whichever of the two comes up
  // alternates strictly, starting with a key, so a door never appears
  // before there's a key in play to open it. Doors can't land on an "O"
  // piece (its 2x2 shape has no natural single "gate" cell — a door
  // blocking one corner of a solid block reads oddly) — the roll falls back
  // to key/chest instead of being wasted.
  specialCellChancePerPiece: 0.15,
  // How close a hero needs to be to pick up a key, open a door, or trigger
  // a chest.
  heroItemPickupRadius: 0.6,
  chestGoldAmount: 60,

  // --- explosive piece ---
  // The *upcoming* piece (shown in the "next piece" preview) has this
  // chance of being a 1-tile explosive instead of a normal tetromino.
  // Moving/rotating it into a foundation destroys the foundation instead of
  // being blocked (see dungeon.ts's architectMove/architectRotate) — it
  // still has to be placed normally afterward, same as any piece, and can
  // only ever destroy foundations, never already-built floor.
  explosivePieceChance: 0.1,

  // --- placed-tile hazard glyphs ---
  // Each newly-placed *vanilla* cell (not a door/key/chest) has this chance
  // of becoming a hazard glyph — visible to everyone, same spirit as "we
  // always want to draw AOEs".
  glyphTileChancePerPlacedTile: 0.08,
  // Of the tiles that do become a glyph, this fraction are the continuous
  // "dot" hazard (damages any hero standing on it, indefinitely); the rest
  // are the "burst" trap (one lump of damage to the first hero who steps on
  // it, then it's consumed — reverts to plain floor).
  glyphTileDotChanceGivenGlyph: 0.5,
  glyphBurstDamage: 30,
  glyphDotDamagePerSecond: 6,
  // Completing a tetris line converts this fraction of that row's
  // still-vanilla tiles into hazard glyphs (design doc's line-clear gold,
  // now with a hazard trade-off attached).
  glyphTileLineClearConversionProportion: 0.8,

  // --- hero death & resurrection ---
  // Death is no longer permanent (an earlier v1 simplification — see
  // doc/game_design.md) — a dead hero always loses any level-up progress
  // (reverts to level 1, base class stats) but comes back one of two ways:
  // an automatic timer, or a teammate reviving the corpse in place. Either
  // way they come back at a fraction of max hp, not full.
  heroReviveHpFraction: 0.5,
  // Auto-respawn: a dead hero who isn't rescued first comes back on their
  // own after this long, next to whichever living hero is furthest along
  // (not at their own death spot, which could now be well behind the flame
  // barrier) — guarantees a re-spawn that isn't just an instant re-death.
  heroAutoRespawnSeconds: 45,
  // Corpse revive: a living hero standing within heroCorpseReviveRadius
  // tiles of a corpse for this many *continuous* seconds revives it right
  // there in place — stepping away resets the progress. Can't be *started*
  // once the hero has been dead longer than heroCorpseReviveDeadlineSeconds
  // (an already-started channel isn't cut off by crossing that deadline
  // mid-channel, only a fresh attempt after it is blocked).
  heroCorpseReviveSeconds: 5,
  heroCorpseReviveDeadlineSeconds: 10,
  heroCorpseReviveRadius: 1.2,
  // Not part of the design doc — a playtesting/UX safety net (same spirit as
  // CLAUDE.md's "Debug tools"): without it, a hero who respawns into a
  // crowded fight or a flame barrier that's crept forward during their 45s
  // wait would often just die again instantly, making the mechanic feel
  // pointless. A brief window of immunity after *any* respawn (auto or
  // corpse-revive) gives them a chance to actually reposition.
  heroRespawnInvulnerableSeconds: 2,

  // --- debug / God Mode tools (not part of the design doc — see
  // CLAUDE.md's "Debug tools") ---
  debugGoldGrantAmount: 100,
} as const;
